import { BOSSES, LAND, STRUCTURES } from '@somnhold/content';
import { cellCenter, cellHex } from '../grid.ts';
import { hexLen, hexToWorld } from '../hex.ts';
import { idiv, imax, withinRange } from '../math/fixed.ts';
import { rngChance, rngInt } from '../math/rng.ts';
import type { BattleState, Rift, Spawn } from '../types.ts';
import { spawnPoint } from './create.ts';

/*
 * Fissures mid-wave (docs/CONSTRUCCION.md §6). On terraced land the wave opens with its fissures, and heroes
 * learned to stand on them and cut each nightmare down as it climbed out. Now the general watches the field
 * the whole wave: when heroes stand by a fissure that still has nightmares to let out (or now and then just
 * because), the ground trembles somewhere no hero is, where the towers reach least, and a new fissure opens
 * there; what was coming out of the watched one comes out of the new one, and the old one closes. As many
 * times as it takes (2026-11-05: with a cap of four, heroes just waited on the last one). Sometimes a second
 * one trembles too: a decoy, which lets a few out and closes. Nobody can tell them apart while they tremble.
 */

const HEX = 1000;

/** Where a sector's nightmares come out now: the rift it was sent to once open, else its own fissure. */
const sourceOf = (s: BattleState, sector: number): { x: number; y: number } => {
  const st = s.rifts;
  const i = st ? (st.from[sector] ?? -1) : -1;
  const r = i >= 0 ? st?.list[i] : undefined;
  return r ? { x: r.x, y: r.y } : spawnPoint(s, sector);
};

/** A spawn the general may send elsewhere: not a boss, nor one of the Unsleeping, nor a plan's squad, nor one placed by hand. */
const movable = (sp: Spawn): boolean => !(sp.kind in BOSSES) && !sp.boss && !sp.un && sp.squad === undefined && !sp.at;

/** Fire per second the standing towers pour on a point (by their base reach). */
const fireAt = (s: BattleState, x: number, y: number): number => {
  let fire = 0;
  for (const t of s.structures) {
    if (t.destroyed || t.hp <= 0) continue;
    const def = STRUCTURES[t.type];
    if (def.category !== 'tower' || def.reload <= 0) continue;
    const c = hexToWorld(t.q, t.r);
    if (!withinRange(c.x, c.y, x, y, def.range)) continue;
    fire += idiv((def.damage[t.level - 1] ?? 0) * 12, def.reload);
  }
  return fire;
};

/**
 * The best spot for a rift among a spread sample of the wave's spots, clear of every standing hero and of the
 * fissures already open: `near` false, where the towers reach least and the walk is short (a real one);
 * true, the one nearest the heroes (a decoy, to draw them). -1 when none is clear.
 */
const pickSpot = (s: BattleState, taken: { x: number; y: number }[], near: boolean): number => {
  const spots = s.map.land?.spots ?? [];
  const n = spots.length;
  if (n === 0) return -1;
  const k = LAND.rifts;
  const step = imax(1, idiv(n, k.sample));
  const start = rngInt(s.rng, step);
  const R = s.map.radius;
  const standing = s.heroes.filter((h) => h.downTicks === 0 && h.hp > 0);
  const clear = k.heroClearHexes * HEX;
  const apart = LAND.fissureApart * HEX;
  let best = -1;
  let bestScore = 0;
  for (let i = start; i < n; i += step) {
    const id = spots[i] as number;
    const c = cellCenter(R, id);
    if (standing.some((h) => withinRange(h.x, h.y, c.x, c.y, clear))) continue;
    if (taken.some((t) => withinRange(t.x, t.y, c.x, c.y, apart))) continue;
    let score: number;
    if (near) {
      score = 0x7fffffff;
      for (const h of standing) {
        const dx = idiv(h.x - c.x, 100);
        const dy = idiv(h.y - c.y, 100);
        const d = dx * dx + dy * dy;
        if (d < score) score = d;
      }
    } else {
      const h = cellHex(R, id);
      const walk = hexLen(h.q, h.r);
      score = fireAt(s, c.x, c.y) * k.fireW + walk * k.walkW;
    }
    if (best < 0 || score < bestScore) {
      best = id;
      bestScore = score;
    }
  }
  return best;
};

/** Every sector's spawns still owed to the wave (from the queue's head). */
const owedBySector = (s: BattleState): number[] => {
  const out = new Array<number>(s.map.sectors.length).fill(0);
  const w = s.wave;
  for (let i = w.next; i < w.spawns.length; i++) {
    const sp = w.spawns[i] as Spawn;
    if (movable(sp) && sp.sector >= 0 && sp.sector < out.length) out[sp.sector] = (out[sp.sector] as number) + 1;
  }
  return out;
};

export const riftsTick = (s: BattleState): void => {
  if (!s.rules?.rifts || !s.map.land?.open || !s.started) return;
  const k = LAND.rifts;
  const st = (s.rifts ??= { list: [], from: s.map.sectors.map(() => -1), next: k.thinkTicks, last: -k.cooldownTicks, nextId: 1 });
  const t = s.waveT;
  // Rifts that tremble open now and take their sectors; one no sector climbs from any more closes (a spent decoy too).
  st.list.forEach((r, i) => {
    if (t !== r.open || r.sectors.length === 0) return;
    for (const sec of r.sectors) {
      const was = st.from[sec] ?? -1;
      st.from[sec] = i;
      const old = was >= 0 ? st.list[was] : undefined;
      if (old && old.close === 0 && !st.from.includes(was)) old.close = t + k.thinkTicks;
    }
  });
  if (t < st.next) return;
  st.next = t + k.thinkTicks;
  const real = st.list.filter((r) => !r.decoy).length;
  if (real >= k.maxPerWave || t - st.last < k.cooldownTicks) return;
  // Nothing new while one still trembles.
  if (st.list.some((r) => t < r.open)) return;
  const owed = owedBySector(s);
  let total = 0;
  for (const o of owed) total += o;
  if (total < k.minOwed) return;
  const standing = s.heroes.filter((h) => h.downTicks === 0 && h.hp > 0);
  const camp = k.campHexes * HEX;
  const watched: number[] = [];
  owed.forEach((o, sec) => {
    if (o === 0) return;
    const p = sourceOf(s, sec);
    if (standing.some((h) => withinRange(h.x, h.y, p.x, p.y, camp))) watched.push(sec);
  });
  // Now and then just because: the busiest sector moves (so standing back isn't a rule to learn either).
  if (watched.length === 0) {
    if (!rngChance(s.rng, k.whimBp)) return;
    let busiest = 0;
    for (let i = 1; i < owed.length; i++) if ((owed[i] as number) > (owed[busiest] as number)) busiest = i;
    watched.push(busiest);
  }
  const taken = [...s.map.sectors.map((_, i) => spawnPoint(s, i)), ...st.list.filter((r) => !r.close || t < r.close).map((r) => ({ x: r.x, y: r.y }))];
  const id = pickSpot(s, taken, false);
  if (id < 0) return;
  const c = cellCenter(s.map.radius, id);
  const open = t + k.warnTicks;
  const rift: Rift = { id: st.nextId++, x: c.x, y: c.y, warn: t, open, close: 0, decoy: false, owed: 0, sectors: watched };
  st.list.push(rift);
  st.last = t;
  // A decoy trembles at the same time, nearer the heroes: it lets a few out and closes.
  if (rngChance(s.rng, k.decoyBp)) {
    const did = pickSpot(s, [...taken, c], true);
    if (did >= 0) {
      const d = cellCenter(s.map.radius, did);
      st.list.push({ id: st.nextId++, x: d.x, y: d.y, warn: t, open, close: 0, decoy: true, owed: k.decoyOwed, sectors: [] });
    }
  }
};

/**
 * Where a spawn coming due climbs out: an open decoy still owed some takes it first; else the rift its sector
 * was sent to; else null (its own fissure). A spent decoy closes a moment later.
 */
export const riftSpawnAt = (s: BattleState, sp: Spawn): { x: number; y: number } | null => {
  const st = s.rifts;
  if (!st || !movable(sp)) return null;
  const t = s.waveT;
  for (const r of st.list) {
    if (!r.decoy || r.owed <= 0 || t < r.open) continue;
    r.owed--;
    if (r.owed === 0) r.close = t + LAND.rifts.thinkTicks;
    return { x: r.x, y: r.y };
  }
  const i = st.from[sp.sector] ?? -1;
  const r = i >= 0 ? st.list[i] : undefined;
  return r ? { x: r.x, y: r.y } : null;
};
